/* Main-PDP progressive SKU preview. Server responses remain purchase authority. */
if (!customElements.get('sku-switch-optimizer')) {
  customElements.define('sku-switch-optimizer', class extends HTMLElement {
    connectedCallback() {
      const mount = this.mount = {};
      Promise.all(['product-info', 'variant-selects', 'media-gallery'].map(n => customElements.whenDefined(n)))
        .then(() => { if (this.isConnected && this.mount === mount) this.start(); })
        .catch(error => console.error('SKU preview initialization failed; legacy retained.', error));
    }

    configFrom(root) {
      const data = JSON.parse(root.querySelector('[data-sku-preview-index]')?.textContent || 'null');
      // Version 1 remains readable during the two-asset rolling deployment/rollback.
      if (!data || ![1, 2].includes(data.version) || !/^\d+$/.test(data.productId) || !Array.isArray(data.variants) || !Number.isInteger(data.expectedCount) || data.expectedCount < 2 || data.expectedCount > 250 || data.variants.length !== data.expectedCount) throw new Error('Incomplete preview index');
      const optionCount = data.version === 1 ? 2 : data.optionCount;
      if (![1, 2, 3].includes(optionCount)) throw new Error('Invalid option count');
      const ids = new Set(data.variants.map(v => String(v.id)));
      if (ids.size !== data.expectedCount || [...ids].some(id => !/^\d+$/.test(id))) throw new Error('Invalid variant IDs');
      if (data.version === 1 && (data.expectedIds?.length !== data.expectedCount || data.expectedIds.some(id => !ids.has(id)))) throw new Error('Preview catalog changed');
      if (data.variants.some(v => typeof v.available !== 'boolean' || !Array.isArray(v.options) || !Array.isArray(v.optionIds) || v.options.length !== optionCount || v.optionIds.length !== optionCount || v.optionIds.some(id => !/^\d+$/.test(id)))) throw new Error('Invalid preview options');
      if (new Set(data.variants.map(v => v.optionIds.join(','))).size !== data.expectedCount) throw new Error('Duplicate option combinations');
      return data;
    }

    start() {
      const p = this.p = this.closest('product-info');
      if (!p || p.dataset.updateUrl !== 'true' || p.dataset.viewMode === 'quick-view' || !p.id.startsWith('MainProduct-')) return;
      try { this.config = this.configFrom(this); } catch (e) { console.warn(e.message); return; }
      this.gallery = p.querySelector('media-gallery');
      if (this.config.productId !== p.dataset.productId || !p.currentVariant || !this.gallery?.initialized || p._skuOptimizer) return;
      this.confirmed = p.currentVariant;
      this.confirmedIds = this.row(this.confirmed.id)?.optionIds;
      if (!this.confirmedIds || !this.controlsCanRepresent(this.confirmedIds)) return;
      p._skuOptimizer = this;
      this.generation = 0;
      this.pending = false;
      this.invalid = false;
      this.status = document.createElement('span');
      this.status.className = 'sku-preview-status';
      this.status.setAttribute('role', 'status');
      this.status.setAttribute('aria-live', 'polite');
      p.querySelector('variant-selects').after(this.status);
      this.originalEnable = p.enableButtons;
      this.originalURL = p.updateURL;
      this.originalMedia = p.updateMedia;
      p.enableButtons = (...args) => { if (!this.pending && !this.invalid) this.originalEnable.apply(p, args); };
      p.updateURL = (url, id) => {
        if (p.dataset.updateUrl === 'false') return;
        const target = new URL(url, location.origin);
        const current = new URL(location.href);
        current.pathname = target.pathname;
        target.searchParams.forEach((value, key) => current.searchParams.set(key, value));
        current.searchParams.delete('option_values');
        current.searchParams.delete('section_id');
        if (id) current.searchParams.set('variant', String(id)); else current.searchParams.delete('variant');
        history.replaceState({}, '', current.href);
      };
      p.updateMedia = variant => {
        const ids = this.row(variant.id)?.optionIds || p.variantSelectors?.selectedOptionValues || [];
        // showMedia cancels stale image jobs even when the visible signature matches.
        this.showMedia(variant, ids);
      };
      this.abortListeners = new AbortController();
      const opts = { capture: true, signal: this.abortListeners.signal };
      document.addEventListener('change', e => this.change(e), opts);
      document.addEventListener('submit', e => this.guard(e), opts);
      document.addEventListener('click', e => this.guard(e), opts);
      this.attachSticky();
      p.dataset.skuPreviewState = 'ready';
    }

    row(id) { return this.config.variants.find(v => v.id === String(id)); }
    async showMedia(variant, ids) {
      const token = this.imageToken = (this.imageToken || 0) + 1;
      const generation = this.generation;
      const selection = this.gallery.resolveGallerySelection(variant, ids);
      if (selection.signature === this.gallery.activeMediaSignature && selection.signature !== 'fallback') {
        this.imageBusy = false;
        this.imageFailed = false;
        this.previewId = String(variant.id);
        return;
      }
      const firstId = selection.mediaIds?.[0] || variant.featured_media?.id || this.gallery.canonicalMedia[0]?.dataset.mediaId;
      const source = this.gallery.canonicalMedia.find(n => String(n.dataset.mediaId) === String(firstId))?.querySelector('img');
      this.imageBusy = !!source;
      let timer, decoded;
      try {
        if (source) {
          // Decode the responsive first image before removing the last visible image.
          // This is a visual cache only; it never caches commerce responses.
          this.imageCache ||= new Map();
          const key = source.src + ':' + innerWidth + ':' + devicePixelRatio;
          if (!this.imageCache.has(key)) {
            const image = new Image();
            image.fetchPriority = 'high';
            image.sizes = source.sizes; image.srcset = source.srcset; image.src = source.src;
            const promise = image.decode().then(() => image); promise.catch(() => this.imageCache.delete(key));
            this.imageCache.set(key, promise);
            if (this.imageCache.size > 12) this.imageCache.delete(this.imageCache.keys().next().value);
          }
          decoded = await Promise.race([this.imageCache.get(key), new Promise((_, reject) => {timer=setTimeout(()=>reject(new Error('Image timeout')),8000)})]);
        }
        if (!this.isCurrent(generation) || token !== this.imageToken) return;
        // On static native galleries, reset the original order before moving a featured
        // item. Reindex zoom afterwards; otherwise repeated switches accumulate order drift.
        const staticFallback = selection.signature === 'fallback' && !this.gallery.sliderInstance?.slider;
        if (staticFallback && this.gallery.elements.mediaList) {
          const order = new Map(this.gallery.canonicalMedia.map((n, i) => [n.dataset.mediaId, i]));
          [...this.gallery.elements.mediaList.children].sort((a, b) => (order.get(a.dataset.mediaId) ?? 0) - (order.get(b.dataset.mediaId) ?? 0)).forEach(n => this.gallery.elements.mediaList.append(n));
        }
        this.gallery.setActiveMedia(variant, ids);
        if (staticFallback) {
          this.gallery.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)').forEach((n, i) => {
            n.dataset.mediaIndex = String(i);
            n.querySelectorAll('.product__media-zoom, .js-photoswipe--zoom').forEach(b => b.dataset.mediaIndex = String(i));
          });
          if (this.gallery.enableImageZoom) this.gallery.initImageZoom();
        }
        const target = [...this.gallery.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')].find(n => String(n.dataset.mediaId) === String(firstId));
        let first = target?.dataset.mediaType === 'image' ? target.querySelector('img') : null;
        if (first && decoded) {
          // Reuse the decoded native image, rather than a new image-lazy instance
          // which would hide the cached image again until its load handler runs.
          for (const attr of first.attributes) if (!['is','src','srcset','sizes','loading'].includes(attr.name)) decoded.setAttribute(attr.name,attr.value);
          decoded.classList.add('loaded'); first.replaceWith(decoded); first = decoded;
        }
        if (first) {
          first.style.opacity = '1';
          const motion = first.closest('motion-element');
          if (motion) { motion.classList.remove('loading'); motion.classList.add('loaded'); motion.style.setProperty('transform','none','important'); motion.style.setProperty('opacity','1','important'); }
        }
        this.imageBusy = false;
        this.imageFailed = false;
        this.previewId = String(variant.id);
        if (!this.pending && [this.config.imageLoading,this.config.imageError].includes(this.status.textContent)) this.status.textContent = '';
      } catch (error) {
        if (!this.isCurrent(generation) || token !== this.imageToken) return;
        this.imageBusy = false;
        this.imageFailed = true;
        this.status.textContent = this.config.imageError;
        console.warn('SKU preview image unavailable; previous image retained.', error);
      } finally { clearTimeout(timer); }
    }
    groups() { return [...this.p.querySelector('variant-selects').querySelectorAll('.product-form__input')]; }
    sticky() {
      const s = document.querySelector('#shopify-section-sticky-atc-bar sticky-atc-bar');
      return s?.dataset.productId === this.p.dataset.productId && s.select && typeof s.updateQuantityInput === 'function' ? s : null;
    }
    controlsCanRepresent(ids) {
      return this.groups().length === ids.length && this.groups().every((g, i) => [...g.querySelectorAll('[data-option-value-id]')].some(el => el.dataset.optionValueId === ids[i]));
    }
    setSelection(ids) {
      if (!this.controlsCanRepresent(ids)) return false;
      this.groups().forEach((g, i) => {
        let selected;
        g.querySelectorAll('[data-option-value-id]').forEach(el => {
          const on = el.dataset.optionValueId === ids[i];
          if (el.tagName === 'OPTION') { el.selected = on; el.toggleAttribute('selected', on); }
          else if (el.matches('input[type="radio"]')) el.checked = on;
          if (on) selected = el;
        });
        const label = g.querySelector('[data-selected-swatch-value], [data-selected-value]');
        if (label && !label.querySelector('.swatch')) label.textContent = selected.value;
        if (selected.tagName === 'OPTION') this.p.variantSelectors.updateSelectedSwatchValue({target:selected.closest('select')});
      });
      return true;
    }

    attachSticky() {
      const s = this.sticky();
      if (!s || this.boundSticky === s) return;
      this.boundSticky = s;
      this.originalQuantity = s.updateQuantityInput;
      // Keep quantity rules scoped and ignore stale responses from an older confirmed SKU.
      s.updateQuantityInput = async variantId => {
        const id = String(variantId || s.select.value);
        const generation = this.generation;
        try {
          const url = this.requestURL(id, s.sectionId);
          const response = await fetch(url);
          if (!response.ok) throw new Error('Sticky quantity response ' + response.status);
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          if (!s.isConnected || generation !== this.generation || String(this.confirmed.id) !== id || this.pending) return;
          const fresh = html.getElementById('QuantitySticky-' + s.sectionId);
          const current = s.querySelector('[id^="QuantitySticky-"]');
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        } catch (e) { console.warn('SKU preview sticky quantity update failed.', e); }
      };
    }

    change(event) {
      if (!this.p.isConnected) return;
      const sticky = this.sticky();
      if (sticky && event.target === sticky.select) {
        const candidate = this.row(event.target.value);
        if (!candidate?.available) return;
        event.stopImmediatePropagation();
        this.select(candidate, candidate.optionIds);
        return;
      }
      const picker = event.target.closest?.('variant-selects');
      if (!picker || picker !== this.p.variantSelectors) return;
      const el = event.target.tagName === 'SELECT' ? event.target.selectedOptions[0] : event.target;
      const group = el.closest('.product-form__input');
      const position = this.groups().indexOf(group);
      if (position < 0 || !el.dataset.optionValueId) return;
      // Keep native unavailable-value restoration and combined-product swap behavior.
      if (el.getAttribute('aria-disabled') === 'true') return;
      if (el.dataset.productUrl && new URL(el.dataset.productUrl, location.origin).pathname !== new URL(this.p.dataset.url, location.origin).pathname) {
        // A combined-product navigation must not race an in-flight local confirmation.
        event.stopImmediatePropagation();
        this.controller?.abort();
        const url = new URL(location.href);
        url.pathname = new URL(el.dataset.productUrl, location.origin).pathname;
        url.searchParams.delete('variant'); url.searchParams.delete('option_values');
        this.lock(); location.assign(url.href); return;
      }
      event.stopImmediatePropagation();
      const intent = [...(this.intentIds || this.confirmedIds)];
      intent[position] = el.dataset.optionValueId;
      const candidate = this.candidateFor(intent, position);
      this.select(candidate, intent.slice(0, position + 1));
    }

    candidateFor(intent, position) {
      return this.config.variants.find(v => v.available && v.optionIds.every((id, i) => id === intent[i])) ||
        this.config.variants.find(v => v.available && intent.slice(0, position + 1).every((id, i) => v.optionIds[i] === id));
    }

    requestURL(id, section = this.p.sectionId, prefix) {
      const url = new URL(location.href);
      url.pathname = new URL(this.p.dataset.url, location.origin).pathname;
      url.hash = '';
      url.searchParams.delete('variant');
      url.searchParams.delete('option_values');
      url.searchParams.set('section_id', section);
      if (id) url.searchParams.set('variant', String(id));
      else url.searchParams.set('option_values', prefix.join(','));
      return url;
    }

    lock() {
      this.attachSticky();
      if (!this.pending && !this.invalid) {
        this.buttonStates = new Map();
        this.expressStates = new Map();
        this.p.querySelectorAll('.shopify-payment-button').forEach(el => this.expressStates.set(el, {inert:el.inert, hidden:el.getAttribute('aria-hidden')}));
        for (const root of [this.p, this.sticky()].filter(Boolean)) {
          root.querySelectorAll('[name="add"]').forEach(b => this.buttonStates.set(b, {disabled:b.disabled, aria:b.getAttribute('aria-disabled')}));
        }
      }
      this.pending = true;
      this.p.dataset.skuPreviewState = 'pending';
      this.p.setAttribute('aria-busy', 'true');
      this.p.querySelectorAll('.shopify-payment-button').forEach(el => { el.inert = true; el.setAttribute('aria-hidden', 'true'); });
      const s = this.sticky();
      s?.closest('.sticky-atc-bar')?.setAttribute('data-sku-preview-state', 'pending');
      for (const root of [this.p, s].filter(Boolean)) root.querySelectorAll('[name="add"]').forEach(b => { b.disabled = true; b.setAttribute('aria-disabled', 'true'); });
      this.status.textContent = this.config.updating;
    }

    unlock(serverDisabled, restore = false) {
      this.pending = false;
      this.p.removeAttribute('aria-busy');
      this.p.dataset.skuPreviewState = (!restore && serverDisabled) ? 'unavailable' : 'ready';
      this.sticky()?.closest('.sticky-atc-bar')?.removeAttribute('data-sku-preview-state');
      for (const root of [this.p, this.sticky()].filter(Boolean)) root.querySelectorAll('[name="add"]').forEach(b => {
        const saved = this.buttonStates?.get(b);
        b.disabled = restore ? (saved?.disabled ?? true) : serverDisabled;
        if (saved?.aria !== null && saved?.aria !== undefined) b.setAttribute('aria-disabled', saved.aria);
        else b.removeAttribute('aria-disabled');
      });
      this.buttonStates = null;
      this.expressStates?.forEach((saved, el) => {
        el.inert = (!restore && serverDisabled) || saved.inert;
        if (saved.hidden !== null) el.setAttribute('aria-hidden', saved.hidden); else el.removeAttribute('aria-hidden');
      });
      this.expressStates = null;
    }

    guard(event) {
      const form = event.type === 'submit' ? event.target : event.target.closest?.('form');
      const s = this.sticky();
      const belongs = form && (this.p.contains(form) || s?.contains(form)) && form.matches('form[is="product-form"]');
      if (!belongs) return;
      if (event.type === 'click' && !event.target.closest('[type="submit"], [name="add"], .shopify-payment-button')) return;
      const id = form.querySelector('[name="id"]')?.value;
      const button = form.querySelector('[name="add"]');
      if (this.pending || this.invalid || id !== String(this.confirmed.id) || !this.confirmed.available || button?.disabled) {
        event.preventDefault(); event.stopImmediatePropagation();
      }
    }

    async select(candidate, prefix) {
      if (this.invalid) return;
      this.controller?.abort();
      this.p.abortController?.abort();
      const generation = ++this.generation;
      const controller = this.controller = new AbortController();
      this.lock();
      this.intentIds = candidate ? [...candidate.optionIds] : null;
      this.previewId = null;
      this.imageFailed = false;
      const canPreview = candidate && this.controlsCanRepresent(candidate.optionIds);
      if (canPreview) this.setSelection(candidate.optionIds);
      // Initiate the authoritative request BEFORE doing synchronous gallery work.
      const responsePromise = fetch(this.requestURL(candidate?.id, this.p.sectionId, prefix), {signal:controller.signal, cache:'no-store'});
      let commitStarted = false;
      const timer = setTimeout(() => controller.abort('timeout'), 10000);
      try {
        if (canPreview) {
          const selection = this.gallery.resolveGallerySelection(candidate, candidate.optionIds);
          if (selection.signature !== 'fallback' || candidate.featured_media?.id) {
            this.showMedia(candidate, candidate.optionIds);
          }
        }
        const response = await responsePromise;
        if (!response.ok) throw new Error('Variant HTTP ' + response.status);
        const text = await response.text();
        if (!this.isCurrent(generation)) return;
        const html = new DOMParser().parseFromString(text, 'text/html');
        const root = html.getElementById(this.p.id);
        if (!root || root.dataset.productId !== this.p.dataset.productId) throw new Error('Wrong product response');
        const config = this.configFrom(root);
        if (config.productId !== this.p.dataset.productId) throw new Error('Wrong preview index product');
        const variant = this.p.getSelectedVariant(root);
        const mainId = 'product-form-' + this.p.dataset.section;
        const freshForm = root.querySelector('#' + CSS.escape(mainId));
        const freshSubmit = freshForm?.querySelector('[name="add"]');
        if (!variant || !config.variants.some(v => v.id === String(variant.id)) || freshForm?.querySelector('[name="id"]')?.value !== String(variant.id) || !freshSubmit) throw new Error('Invalid commerce response');
        const disabled = freshSubmit.disabled || !variant.available;
        const focusedId = this.p.variantSelectors.contains(document.activeElement) ? document.activeElement.id : null;
        commitStarted = true;
        this.config = config;
        this.p.handleUpdateProductInfo(this.p.dataset.url, 'main-product')(html);
        const ids = this.row(variant.id).optionIds;
        if (!this.setSelection(ids)) throw new Error('Confirmed controls missing');
        if (focusedId) this.p.variantSelectors.querySelector('#' + CSS.escape(focusedId))?.focus({preventScroll:true});
        const idFields = this.p.querySelectorAll('form[is="product-form"] [name="id"], form[id^="product-form-installment-"] [name="id"]');
        if (!idFields.length || [...idFields].some(el => el.value !== String(variant.id))) throw new Error('Form consistency failed');
        const sticky = this.sticky();
        if (sticky && sticky.select.value !== String(variant.id)) throw new Error('Sticky consistency failed');
        this.confirmed = variant;
        this.confirmedIds = ids;
        this.intentIds = null;
        this.unlock(disabled);
        this.status.textContent = candidate && candidate.id !== String(variant.id) ? this.config.adjusted : this.imageFailed ? this.config.imageError : this.imageBusy ? this.config.imageLoading : '';
        this.p.dispatchEvent(new CustomEvent('sku-preview:confirmed', {detail:{generation,id:String(variant.id)}}));
      } catch (error) {
        if (!this.isCurrent(generation)) return;
        if (commitStarted) {
          this.invalid = true;
          this.p.dataset.skuPreviewState = 'failed';
          this.status.textContent = this.config.reload + ' ';
          const reload = document.createElement('button');
          reload.type = 'button'; reload.className = 'sku-preview-reload'; reload.textContent = this.config.reload;
          reload.addEventListener('click', () => location.reload()); this.status.append(reload);
        } else {
          this.intentIds = null;
          this.setSelection(this.confirmedIds);
          this.showMedia(this.confirmed, this.confirmedIds);
          if (this.sticky()) this.sticky().select.value = String(this.confirmed.id);
          this.unlock(true, true);
          this.status.textContent = this.config.retry;
        }
        console.warn('SKU preview request failed safely.', error);
      } finally {
        clearTimeout(timer);
        // Consume a request if synchronous preview rendering threw before awaiting it.
        responsePromise.catch(() => {});
        if (this.isCurrent(generation)) this.controller = null;
      }
    }

    isCurrent(generation) { return this.isConnected && this.p.isConnected && generation === this.generation; }

    disconnectedCallback() {
      this.mount = null;
      this.generation = (this.generation || 0) + 1;
      this.controller?.abort(); this.abortListeners?.abort();
      if (this.p?._skuOptimizer === this) {
        this.p.enableButtons = this.originalEnable;
        this.p.updateURL = this.originalURL;
        this.p.updateMedia = this.originalMedia;
        delete this.p._skuOptimizer;
      }
      if (this.boundSticky && this.originalQuantity) this.boundSticky.updateQuantityInput = this.originalQuantity;
      this.status?.remove();
    }
  });
}
